import { ReactElement } from 'react';
import { ButtonGroup, Flex, Button } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from 'assets/icons/v2';

/**
 * Props for the Pagination component
 * @param totalPages - Total number of pages available
 * @param currentPage - Currently active page number
 * @param onPageChange - Callback function when page changes
 */
type Props = {
  totalPages: number;
  currentPage: number;
  onPageChange: (newPage: number) => void;
  noPadding?: boolean;
};

/**
 * Calculates the page number to jump to when clicking ellipsis
 * @param ellipsisIndex - 1 for left ellipsis, 2 for right ellipsis
 * @param current - Current page number
 * @param total - Total number of pages
 * @returns The page number to jump to
 */
const getJumpPage = (ellipsisIndex: number, current: number, total: number): number => {
  // If it's the first ellipsis (backwards), show previous set of numbers
  if (ellipsisIndex === 1) {
    return Math.max(1, current - 3);
  }
  // If it's the second ellipsis (forwards), show next set of numbers
  return Math.min(total, current + 3);
};

/**
 * Determines which page numbers should be visible in the pagination
 * The logic follows these rules:
 * 1. For 7 or fewer pages, show all pages
 * 2. Always show first and last page
 * 3. Show a window of 5 pages around current page when possible
 * 4. Use ellipsis to indicate skipped pages
 *
 * Examples of output:
 * - [1, 2, 3, 4, 5, 6, 7] (total <= 7)
 * - [1, 'ellipsis-left', 4, 5, 6, 'ellipsis-right', 10] (middle)
 * - [1, 2, 3, 4, 5, 'ellipsis-right', 10] (start)
 * - [1, 'ellipsis-left', 6, 7, 8, 9, 10] (end)
 */
const getVisiblePages = (current: number, total: number): (number | string)[] => {
  // For 7 or fewer pages, show all page numbers
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  // Configure the sliding window size
  const window = 5;
  const halfWindow = Math.floor(window / 2);

  // Calculate initial start and end of the window
  let start = Math.max(2, current - halfWindow);
  let end = Math.min(total - 1, start + window - 1);

  // Adjust window position when near the edges
  // Adjust start if end is pushed to max
  if (end === total - 1) {
    start = Math.max(2, end - window + 1);
  }
  // Adjust end if start is pushed to min
  if (start === 2) {
    end = Math.min(total - 1, start + window - 1);
  }

  // Build the array of visible page numbers and ellipsis
  const pages: (number | string)[] = [1]; // Always show first page
  if (start > 2) pages.push('ellipsis-left');

  // Add all pages within the window
  // eslint-disable-next-line no-plusplus
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  // Add right ellipsis and last page if needed
  if (end < total - 1) pages.push('ellipsis-right');
  if (total > 1) pages.push(total);

  return pages;
};

/**
 * Pagination component that displays a list of page numbers with ellipsis
 * for navigating through multiple pages. Supports large numbers of pages
 * by showing a sliding window of page numbers around the current page.
 */
export const Pagination = ({
  totalPages,
  currentPage,
  onPageChange,
  noPadding,
}: Props): ReactElement => {
  const visiblePages = getVisiblePages(currentPage, totalPages);

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      width="100%"
      py={noPadding ? undefined : '11px'}
    >
      <ButtonGroup gap="7px">
        <Button
          aria-label="Vorherige Seite"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          variant="tertiary"
          minW="26px"
          w="26px"
          h="27px"
          minH="27px"
          p="0"
          color="text.primary"
          _hover={{ bg: 'background.canvas', color: 'text.primary' }}
          _active={{ bg: 'background.canvas', color: 'text.primary' }}
          textStyle="body.s.medium"
        >
          <ChevronLeftIcon />
        </Button>

        {visiblePages.map((page) =>
          page === 'ellipsis-left' || page === 'ellipsis-right' ? (
            <Button
              key={`${page}`}
              variant="tertiary"
              minW="26px"
              w="26px"
              h="27px"
              minH="27px"
              p="0"
              color="text.primary"
              _hover={{ bg: 'background.canvas', color: 'text.primary' }}
              _active={{ bg: 'background.canvas', color: 'text.primary' }}
              onClick={() =>
                onPageChange(getJumpPage(page === 'ellipsis-left' ? 1 : 2, currentPage, totalPages))
              }
              aria-label={page === 'ellipsis-left' ? 'Vorherige Seiten' : 'Nächste Seiten'}
              textStyle="body.s.medium"
            >
              •••
            </Button>
          ) : (
            <Button
              key={page}
              aria-label={`Seite ${page}`}
              variant="tertiary"
              minW="26px"
              w="26px"
              h="27px"
              minH="27px"
              p="0"
              bg={currentPage === page ? 'background.highlight.green' : 'transparent'}
              color="text.primary"
              borderWidth={currentPage === page ? '1px' : '0'}
              borderStyle="solid"
              borderColor={currentPage === page ? 'border.accent.active' : 'transparent'}
              borderRadius={currentPage === page ? '5px' : undefined}
              onClick={() => onPageChange(page as number)}
              textStyle="body.s.medium"
            >
              {page}
            </Button>
          )
        )}

        <Button
          aria-label="Nächste Seite"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          variant="tertiary"
          minW="26px"
          w="26px"
          h="27px"
          minH="27px"
          p="0"
          color="text.primary"
          _hover={{ bg: 'background.canvas', color: 'text.primary' }}
          _active={{ bg: 'background.canvas', color: 'text.primary' }}
          textStyle="body.s.medium"
        >
          <ChevronRightIcon />
        </Button>
      </ButtonGroup>
    </Flex>
  );
};
